import React, { useState, useEffect, useRef } from 'react';
import apiService from '../../services/api.service';
import VisitorTable from '../../components/VisitorTable';
import VisitorDetailsModal from '../../components/VisitorDetailsModal';
import VisitorForm from '../../components/VisitorForm';
import { generateVisitorQRCode, generateBulkQRCodes } from '../../utils/qrCodeGenerator';
import { replacePlaceholders, generateBulkBadgesHtml } from '../../utils/templateReplacer';
import { resolveVisitorPhotoSrc } from '../../utils/visitorMedia';
import './Visitors.css';

const Visitors = () => {
  const templateRef = useRef(null);
  const [visitors, setVisitors] = useState([]);
  const [filteredVisitors, setFilteredVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingVisitorId, setEditingVisitorId] = useState(null);
  const [sendingSMSId, setSendingSMSId] = useState(null);
  const [sendingWhatsAppId, setSendingWhatsAppId] = useState(null);
  const [selectedVisitors, setSelectedVisitors] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [visitorsPerPage, setVisitorsPerPage] = useState(10);
  const [printTemplate, setPrintTemplate] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    const init = async () => {
      await fetchVisitors();
      if (isMounted) {
        await loadPrintTemplate();
      }
    };
    
    init();
    
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    filterVisitors();
  }, [visitors, searchTerm, statusFilter]);

  const fetchVisitors = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAdminVisitors();
      if (response.data.success) {
        setVisitors(response.data.data);
      } else {
        setError('Failed to load visitors');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load visitors');
      console.error('Visitors fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPrintTemplate = async () => {
    console.log('Loading print template from API...');
    setTemplateLoading(true);
    
    try {
      const response = await apiService.getVisitorBadgeTemplate();
      console.log('API response:', response.data);
      
      if (response.data.success && response.data.data && response.data.data.bodyOfTheTemplate) {
        console.log('Template loaded from API successfully');
        const template = response.data.data.bodyOfTheTemplate;
        console.log('Setting template state, length:', template.length);
        
        // Store in both state and ref
        setPrintTemplate(template);
        templateRef.current = template;
        
        setTemplateLoading(false);
        console.log('Template loading complete');
        return;
      }
    } catch (err) {
      console.error('API error:', err);
    }
    
    // If API fails or returns no template, load default
    console.log('Loading default template...');
    try {
      await loadDefaultPrintTemplate();
    } catch (err) {
      console.error('Failed to load default template:', err);
    }
    
    setTemplateLoading(false);
  };

  const loadDefaultPrintTemplate = async () => {
    const cacheBuster = new Date().getTime();
    const url = `/templates/visitor-badge-default.html?cb=${cacheBuster}`;
    console.log('Fetching default template from:', url);
    
    const response = await fetch(url);
    console.log('Default template fetch response:', response.status, response.ok);
    
    if (response.ok) {
      const html = await response.text();
      console.log('Default template loaded, length:', html.length);
      
      // Store in both state and ref
      setPrintTemplate(html);
      templateRef.current = html;
      
      console.log('Default template state updated');
    } else {
      throw new Error(`Failed to fetch default template: ${response.status}`);
    }
  };

  const filterVisitors = () => {
    let filtered = [...visitors];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase().trim();
      const searchDigits = searchTerm.replace(/\D/g, '');
      filtered = filtered.filter(visitor => {
        const idMatch = String(visitor.id || visitor.vid || '').includes(searchTerm.trim());
        const aadharValue = String(visitor.aadhar || visitor.adhar || '');
        const aadharMatch =
          aadharValue.includes(searchTerm.trim()) ||
          (searchDigits.length > 0 && aadharValue.replace(/\D/g, '').includes(searchDigits));

        return (
          idMatch ||
          aadharMatch ||
          (visitor.vname || visitor.name || '').toLowerCase().includes(searchLower) ||
          (visitor.vphone || visitor.mobile || '').includes(searchLower) ||
          (visitor.firm || '').toLowerCase().includes(searchLower) ||
          (visitor.state || '').toLowerCase().includes(searchLower) ||
          (visitor.city || '').toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(visitor => 
        String(visitor.status) === statusFilter
      );
    }

    setFilteredVisitors(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleDelete = async (visitorId) => {
    try {
      setError(null);
      const response = await apiService.deleteVisitor(visitorId);
      if (response.data.success) {
        setSuccessMessage('Visitor deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchVisitors(); // Refresh the list
      } else {
        setError('Failed to delete visitor');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Visitor not found. It may have already been deleted.');
        // Refresh the list to update the UI
        fetchVisitors();
      } else {
        setError(err.response?.data?.error || 'Failed to delete visitor');
      }
      console.error('Visitor delete error:', err);
    }
  };

  const handleSelectVisitor = (visitorId) => {
    setSelectedVisitors(prev => {
      if (prev.includes(visitorId)) {
        return prev.filter(id => id !== visitorId);
      } else {
        return [...prev, visitorId];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = currentVisitors.map(v => v.id);
      setSelectedVisitors(allIds);
    } else {
      setSelectedVisitors([]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedVisitors.length === 0) {
      setError('No visitors selected');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedVisitors.length} visitor(s)? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setIsDeleting(true);
      setError(null);
      const response = await apiService.bulkDeleteVisitors(selectedVisitors);
      
      if (response.data.success) {
        const { deleted, failed } = response.data;
        let message = `Successfully deleted ${deleted} visitor(s)`;
        if (failed > 0) {
          message += `, ${failed} failed`;
        }
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(''), 3000);
        setSelectedVisitors([]);
        fetchVisitors();
      } else {
        setError('Failed to delete visitors');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to delete visitors');
      console.error('Bulk delete error:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewDetails = async (visitorId) => {
    try {
      setDetailsLoading(true);
      setShowDetailsModal(true);
      setSelectedVisitor(null);
      const response = await apiService.getAdminVisitorById(visitorId);
      if (response.data.success) {
        setSelectedVisitor(response.data.data);
      } else {
        setError('Failed to load visitor details');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load visitor details');
      console.error('Visitor details fetch error:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedVisitor(null);
  };

  const handleAddVisitor = () => {
    setEditingVisitorId(null);
    setShowFormModal(true);
  };

  const handleEditVisitor = (visitorId) => {
    setEditingVisitorId(visitorId);
    setShowFormModal(true);
  };
  const handleSendSMS = async (visitor) => {
    const phone = visitor.vphone || visitor.mobile;
    if (!phone) {
      setError('Visitor does not have a phone number');
      return;
    }
    
    setSendingSMSId(visitor.id);
    setError(null);
    
    try {
      const visitorName = visitor.vname || visitor.name || 'Customer';
      const message = `Dear ${visitorName}, Thank you for registering for SSVASS National Gems & Jewellery Exhibition. Your registration is confirmed. For queries, call: 0612-2675064`;
      const response = await apiService.sendSMS(visitor.id, { message });
      
      console.log('=== SMS API RESPONSE ===');
      console.log('Full Response:', response.data);
      if (response.data.data?.api_url) {
        console.log('\n🔗 SMS Gateway URL (copy to test in browser):');
        console.log(response.data.data.api_url);
        console.log('\n');
      }
      console.log('Gateway Response:', response.data.data?.gateway_response);
      console.log('HTTP Code:', response.data.data?.http_code);
      console.log('========================\n');
      
      if (response.data.success) {
        setSuccessMessage('SMS sent successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to send SMS');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send SMS');
      console.error('SMS send error:', err);
    } finally {
      setSendingSMSId(null);
    }
  };

  const handleSendWhatsApp = async (visitor) => {
    const phone = visitor.vphone || visitor.mobile;
    if (!phone) {
      setError('Visitor does not have a phone number');
      return;
    }
    
    setSendingWhatsAppId(visitor.id);
    setError(null);
    
    try {
      const visitorName = visitor.vname || visitor.name || 'Customer';
      const message = `Dear ${visitorName}, Thank you for registering for SSVASS National Gems & Jewellery Exhibition. Your registration is confirmed. For queries, call: 0612-2675064`;
      const response = await apiService.sendWhatsApp(visitor.id, { message });
      
      if (response.data.success) {
        setSuccessMessage('WhatsApp message sent successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to send WhatsApp message');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send WhatsApp message');
      console.error('WhatsApp send error:', err);
    } finally {
      setSendingWhatsAppId(null);
    }
  };

  const handlePrintVisitor = async (visitor) => {
    // Use ref as fallback if state is not set
    let template = printTemplate || templateRef.current;
    
    console.log('handlePrintVisitor called');
    console.log('  - templateLoading:', templateLoading);
    console.log('  - printTemplate exists:', !!printTemplate);
    console.log('  - templateRef.current exists:', !!templateRef.current);
    console.log('  - Using template:', !!template);
    
    // If template not loaded and still loading, wait for it
    if (!template && templateLoading) {
      console.log('Template is loading, waiting...');
      setError('Template is loading, please wait...');
      
      // Wait up to 5 seconds for template to load
      for (let i = 0; i < 50; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        template = printTemplate || templateRef.current;
        if (template) {
          console.log('Template loaded after waiting!');
          setError(null);
          break;
        }
      }
    }
    
    // If template not loaded and not loading, try loading it
    if (!template && !templateLoading) {
      console.log('Template missing, reloading...');
      setError('Loading template...');
      await loadPrintTemplate();
      // Wait for state to update
      await new Promise(resolve => setTimeout(resolve, 1000));
      template = printTemplate || templateRef.current;
      setError(null);
    }
    
    const finalTemplate = template || printTemplate || templateRef.current;
    
    if (!finalTemplate) {
      console.error('Print template is null in both state and ref!');
      setError('Print template not loaded. Please refresh the page and try again.');
      return;
    }

    try {
      setIsPrinting(true);
      setError(null);
      console.log('Starting print process for visitor:', visitor.token || visitor.code);
      
      // Generate QR code for visitor
      const qrCode = await generateVisitorQRCode(visitor.token || visitor.code);
      const photoPrintSrc = await resolveVisitorPhotoSrc(visitor.profile_pic || visitor.photo);
      const badgeHtml = replacePlaceholders(finalTemplate, { ...visitor, photoPrintSrc }, qrCode);
      console.log('Template placeholders replaced, HTML length:', badgeHtml.length);
      
      // Open print window
      const printWindow = window.open('', '_blank');
      printWindow.document.write(badgeHtml);
      printWindow.document.close();
      
      // Trigger print
      printWindow.onload = function() {
        printWindow.print();
        printWindow.onafterprint = function() {
          printWindow.close();
        };
      };
    } catch (err) {
      console.error('Print error:', err);
      setError('Failed to print visitor badge: ' + err.message);
    } finally {
      setIsPrinting(false);
    }
  };

  const handleBulkPrintVisitors = async () => {
    if (selectedVisitors.length === 0) {
      setError('Please select at least one visitor to print');
      return;
    }

    // Use ref as fallback
    const template = printTemplate || templateRef.current;
    
    if (!template) {
      setError('Print template not loaded. Please try again.');
      return;
    }

    try {
      setIsPrinting(true);
      
      // Get full visitor data for selected visitors
      const visitorPromises = selectedVisitors.map(id => 
        apiService.getAdminVisitorById(id).catch(err => {
          console.error(`Failed to fetch visitor ${id}:`, err);
          return null;
        })
      );
      
      const visitorResponses = await Promise.all(visitorPromises);
      const visitorsData = await Promise.all(
        visitorResponses
          .filter(response => response && response.data.success)
          .map(async (response) => {
            const visitor = response.data.data;
            const photoPrintSrc = await resolveVisitorPhotoSrc(visitor.profile_pic || visitor.photo);
            return { ...visitor, photoPrintSrc };
          })
      );
      
      if (visitorsData.length === 0) {
        setError('Failed to load visitor data for printing');
        return;
      }

      // Generate QR codes for all visitors
      const tokenNumbers = visitorsData.map(v => v.token || v.code).filter(Boolean);
      const qrCodesMap = await generateBulkQRCodes(tokenNumbers);
      
      // Generate combined HTML for all badges
      const bulkHtml = generateBulkBadgesHtml(template, visitorsData, qrCodesMap);
      
      // Open print window
      const printWindow = window.open('', '_blank');
      printWindow.document.write(bulkHtml);
      printWindow.document.close();
      
      // Trigger print
      printWindow.onload = function() {
        printWindow.print();
        printWindow.onafterprint = function() {
          printWindow.close();
        };
      };

      setSuccessMessage(`Printing ${visitorsData.length} visitor badge(s)...`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Bulk print error:', err);
      setError('Failed to print visitor badges: ' + err.message);
    } finally {
      setIsPrinting(false);
    }
  };

  // Expose functions to window for VisitorTable component
  useEffect(() => {
    window.sendSMS = handleSendSMS;
    window.sendWhatsApp = handleSendWhatsApp;
    window.printVisitor = handlePrintVisitor;
    
    return () => {
      delete window.sendSMS;
      delete window.sendWhatsApp;
      delete window.printVisitor;
    };
  }, []);
  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingVisitorId(null);
  };

  const handleFormSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
    fetchVisitors(); // Refresh the list
  };

  // Pagination logic
  const indexOfLastVisitor = currentPage * visitorsPerPage;
  const indexOfFirstVisitor = indexOfLastVisitor - visitorsPerPage;
  const currentVisitors = filteredVisitors.slice(indexOfFirstVisitor, indexOfLastVisitor);
  const totalPages = Math.ceil(filteredVisitors.length / visitorsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handlePageSizeChange = (e) => {
    setVisitorsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button key="first" onClick={() => paginate(1)} className="page-btn">
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="dots1" className="page-dots">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`page-btn ${currentPage === i ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="dots2" className="page-dots">...</span>);
      }
      pages.push(
        <button key="last" onClick={() => paginate(totalPages)} className="page-btn">
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <div className="visitors-container">
      <div className="visitors-header">
        <h2>Visitors Management</h2>
        <div className="header-actions">
          {selectedVisitors.length > 0 && (
            <>
              <button 
                className="btn btn-danger"
                onClick={handleBulkDelete}
                disabled={isDeleting}
                title={`Delete ${selectedVisitors.length} selected visitor(s)`}
              >
                {isDeleting ? '⏳ Deleting...' : `🗑️ Delete Selected (${selectedVisitors.length})`}
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleBulkPrintVisitors}
                disabled={isPrinting}
                title={`Print ${selectedVisitors.length} visitor badge(s)`}
                style={{marginLeft: '10px'}}
              >
                {isPrinting ? '⏳ Printing...' : `🖨️ Print Selected (${selectedVisitors.length})`}
              </button>
            </>
          )}
          <button
            className="btn btn-secondary" 
            onClick={fetchVisitors}
            disabled={loading}
            title="Refresh visitor list"
          >
            {loading ? '⏳ Loading...' : '🔄 Refresh'}
          </button>
          <button className="btn btn-primary" onClick={handleAddVisitor}>
            + Add Visitor
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <strong>Success:</strong> {successMessage}
        </div>
      )}

      <div className="visitors-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by ID, Aadhar, name, phone, or firm..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-box">
          <label htmlFor="statusFilter">Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="0">Pending</option>
            <option value="1">Active</option>
            <option value="2">Inactive</option>
          </select>
        </div>

        <div className="filter-box">
          <label htmlFor="pageSizeFilter">Per Page:</label>
          <select
            id="pageSizeFilter"
            value={visitorsPerPage}
            onChange={handlePageSizeChange}
            className="filter-select"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
          </select>
        </div>
      </div>

      <div className="visitors-stats">
        <span>
          Showing {currentVisitors.length > 0 ? indexOfFirstVisitor + 1 : 0} - {Math.min(indexOfLastVisitor, filteredVisitors.length)} of {filteredVisitors.length} visitors
          {searchTerm || statusFilter !== 'all' ? ` (filtered from ${visitors.length} total)` : ''}
        </span>
      </div>

      <VisitorTable
        visitors={currentVisitors}
        onDelete={handleDelete}
        onEdit={handleEditVisitor}
        onViewDetails={handleViewDetails}
        loading={loading}
        sendingSMSId={sendingSMSId}
        sendingWhatsAppId={sendingWhatsAppId}
        selectedVisitors={selectedVisitors}
        onSelectVisitor={handleSelectVisitor}
        onSelectAll={handleSelectAll}
      />

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {renderPagination()}
          <button
            className="page-btn"
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {showDetailsModal && (
        <VisitorDetailsModal
          visitor={selectedVisitor}
          onClose={closeDetailsModal}
          loading={detailsLoading}
        />
      )}

      {showFormModal && (
        <VisitorForm
          visitorId={editingVisitorId}
          onClose={closeFormModal}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default Visitors;

